import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { mapRegistrationToModel, Registration } from 'app/features/manual-input-registration/models';
import { ContainerMeasurement } from 'app/features/manual-input-result-registration/models/container-measurement';

import { mapContainerMeasurementToModel } from '../../features/manual-input-result-registration/models/container-measurement';
import { LocationRegistrationModel } from '../features/location-selector/models';
import {
    ApiVersion,
    AuthenticationModel,
    Credentials,
    mapModelToMasterData,
    mapUserInfoFromModel,
    MasterDataModel,
    RegistrationListModel,
    Result,
    UserInfo,
    UserInfoModel,
} from '../models';

@Injectable({ providedIn: 'root' })
export class TerminalClient {
    private httpClient = inject(HttpClient);

    getMasterData(
        { identifier, apiVersion }: { identifier: string; apiVersion: ApiVersion },
        deviceId: string,
        applicationName: string,
    ) {
        return this.httpClient
            .get<MasterDataModel>(
                '/api/v1/masterdata?identifier=' +
                    encodeURIComponent(identifier) +
                    '&apiVersion=' +
                    apiVersion +
                    '&deviceId=' +
                    deviceId +
                    '&applicationName=' +
                    applicationName,
            )
            .pipe(map((masterData) => mapModelToMasterData(masterData)));
    }

    getApiVersion(identifier: string) {
        return this.httpClient.get<ApiVersion>(
            '/api/v1/ApiVersion?identifier=' + encodeURIComponent(identifier),
        );
    }

    sendRegistrations({
        registrations,
        credentials,
        apiVersion,
        identifier,
        deviceId,
        applicationName,
        containerMeasurements,
    }: {
        registrations: Registration[];
        credentials: Credentials;
        identifier: string;
        apiVersion: ApiVersion;
        deviceId: string;
        applicationName: string;
        containerMeasurements: ContainerMeasurement[];
    }): Observable<Result> {
        const registrationListModel: RegistrationListModel = {
            credentials: {
                identifier,
                employeeNumber: credentials.employeeNumber,
                pinCode: credentials.pin,
                apiVersion,
            },
            deviceInfo: {
                deviceId,
                applicationName,
            },
            timezoneOffset: new Date().getTimezoneOffset(),
            registrations: registrations.map(mapRegistrationToModel),
            containerMeasurements: containerMeasurements
                ? containerMeasurements.map(mapContainerMeasurementToModel)
                : [],
        };
        return this.httpClient.post<Result>('/api/v1/registrations', registrationListModel);
    }

    getRowRegistrationsForActivity({
        apiVersion,
        identifier,
        activityNumber,
    }: {
        identifier: string;
        apiVersion: ApiVersion;
        activityNumber: number;
    }): Observable<{ rowRegistrations: LocationRegistrationModel[] | undefined } | undefined> {
        return this.httpClient.get<{ rowRegistrations: LocationRegistrationModel[] | undefined } | undefined>(
            '/api/v1/registrations/rows',
            {
                params: {
                    identifier,
                    apiVersion,
                    activityNumber,
                },
            },
        );
    }

    authenticate({
        credentials: { pin, employeeNumber },
        identifier,
        apiVersion,
    }: {
        credentials: Credentials;
        identifier: string;
        apiVersion: ApiVersion;
    }): Observable<boolean> {
        return this.httpClient.post<boolean>(
            '/api/v1/Authenticate',
            new AuthenticationModel({
                identifier,
                employeeNumber,
                pinCode: pin,
                apiVersion,
            }),
        );
    }

    getUserInfo({
        credentials: { pin, employeeNumber },
        identifier,
        apiVersion,
    }: {
        credentials: Credentials;
        identifier: string;
        apiVersion: ApiVersion;
    }): Observable<undefined | UserInfo> {
        return this.httpClient
            .post<UserInfoModel | null | undefined>(
                '/api/v1/Authenticate/user',
                new AuthenticationModel({
                    identifier,
                    employeeNumber,
                    pinCode: pin,
                    apiVersion,
                }),
            )
            .pipe(map(mapUserInfoFromModel));
    }
}
